// frontend/src/components/common/NotificationBell.jsx - VERSION CORRIGÉE
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaBell, FaVolumeMute, FaVolumeUp, FaWifi, FaExclamationTriangle } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import notificationService from '../../services/notificationService';
import notificationSoundService from '../../services/notificationSoundService';
import { formatDate } from '../../utils/formatters';

const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(notificationSoundService.areSoundsEnabled());
  const [wsConnected, setWsConnected] = useState(false);
  const [dataSource, setDataSource] = useState('cache');
  
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  // 🔥 FONCTION OPTIMISÉE: Chargement des notifications (déplacée avant handleWebSocketEvent)
  const loadNotifications = useCallback(async (forceRefresh = false) => {
    setLoading(true);
    
    try {
      const result = await notificationService.getNotifications(forceRefresh);
      
      if (result.success) {
        setNotifications(result.data);
        setUnreadCount(result.data.filter(n => !n.read).length);
        setDataSource(result.source);
        
        if (result.warning) {
          console.warn('⚠️ Données possiblement obsolètes:', result.warning);
        }
      } else {
        console.error('❌ Erreur chargement notifications:', result.error);
      }
    } catch (error) {
      console.error('❌ Erreur inattendue:', error);
    } finally {
      setLoading(false);
    }
  }, []); // Pas de dépendances pour éviter les re-renders
  
  // 🔥 GESTION des événements WebSocket (dépendances corrigées)
  const handleWebSocketEvent = useCallback((event, data) => {
    console.log(`🔔 Événement WebSocket reçu: ${event}`, data);
    
    switch (event) {
      case 'connected':
        setWsConnected(data);
        if (data) {
          console.log('✅ WebSocket connecté - rechargement des notifications');
          loadNotifications(true);
        }
        break;
        
      case 'new_notification':
        console.log('📬 Nouvelle notification reçue:', data);
        setNotifications(prev => {
          const exists = prev.find(n => n._id === data._id);
          if (exists) return prev;
          return [data, ...prev];
        });
        
        if (!data.read) {
          const soundType = data.type === 'mention' ? 'alert' : 'message';
          notificationSoundService.playNotificationSound(soundType);
          setUnreadCount(prev => prev + 1);
        }
        setDataSource('websocket');
        break;
        
      case 'notification_updated':
      case 'notification_read':
        setNotifications(prev => 
          prev.map(n => 
            n._id === data ? { ...n, read: true } : n
          )
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
        break;
        
      case 'all_notifications_read':
        setNotifications(prev => 
          prev.map(n => ({ ...n, read: true }))
        );
        setUnreadCount(0);
        break;
        
      case 'notification_deleted':
        setNotifications(prev => {
          const deletedNotif = prev.find(n => n._id === data);
          const newNotifications = prev.filter(n => n._id !== data);
          
          // Mettre à jour le compteur si c'était une notification non lue
          if (deletedNotif && !deletedNotif.read) {
            setUnreadCount(current => Math.max(0, current - 1));
          }
          
          return newNotifications;
        });
        break;
        
      default:
        console.log(`🤷 Événement WebSocket non géré: ${event}`);
    }
  }, [loadNotifications]); // ✅ Dépendance ajoutée
  
  // 🔥 Initialisation WebSocket et listeners
  useEffect(() => {
    if (!currentUser) return;
    
    // Initialiser les sons
    notificationSoundService.initSoundService();
    setSoundEnabled(notificationSoundService.areSoundsEnabled());
    
    // Obtenir le token pour WebSocket
    const token = localStorage.getItem('token');
    if (token) {
      notificationService.initWebSocket(token);
    }
    
    // Ajouter le listener pour les événements WebSocket
    const removeListener = notificationService.addListener(handleWebSocketEvent);
    
    // Charger les notifications initiales
    loadNotifications();
    
    // Cleanup
    return () => {
      removeListener();
      notificationService.cleanup();
    };
  }, [currentUser, handleWebSocketEvent, loadNotifications]); // ✅ Dépendances ajoutées
  
  // Fermer le dropdown en cliquant dehors
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // 🔥 Clic sur notification
  const handleNotificationClick = async (notification) => {
    if (!notification.read) {
      try {
        const result = await notificationService.markAsRead(notification._id);
        if (result.success) {
          setNotifications(prev => 
            prev.map(n => 
              n._id === notification._id ? { ...n, read: true } : n
            )
          );
          setUnreadCount(prev => Math.max(0, prev - 1));
        }
      } catch (error) {
        console.error('❌ Erreur marquage lecture:', error);
      }
    }
    
    if (notification.contract) {
      const contractId = notification.contract._id || notification.contract;
      navigate(`/contracts/${contractId}`);
      setIsOpen(false);
    }
  };
  
  // 🔥 Marquer tout comme lu
  const handleMarkAllAsRead = async () => {
    try {
      const result = await notificationService.markAllAsRead();
      if (result.success) {
        setNotifications(prev => prev.map(n => ({ ...n, read: true })));
        setUnreadCount(0);
      }
    } catch (error) {
      console.error('❌ Erreur marquage global:', error);
    }
  };
  
  // Toggle son
  const toggleSound = () => {
    const newState = !soundEnabled;
    setSoundEnabled(newState);
    notificationSoundService.toggleNotificationSounds(newState);
    
    if (newState) {
      notificationSoundService.playNotificationSound('default');
    }
  };
  
  // Couleur de fond notification
  const getNotificationBgColor = (notification) => {
    if (!notification.read) {
      return 'bg-blue-50 border-l-4 border-blue-400';
    }
    
    switch (notification.type) {
      case 'mention':
        return 'bg-yellow-50';
      case 'comment':
        return 'bg-green-50';
      default:
        return '';
    }
  };
  
  // Indicateur de statut
  const getStatusIndicator = () => {
    if (wsConnected) {
      return (
        <div className="flex items-center text-xs text-green-600 mb-1">
          <FaWifi className="mr-1" />
          Temps réel
        </div>
      );
    } else {
      return (
        <div className="flex items-center text-xs text-orange-600 mb-1">
          <FaExclamationTriangle className="mr-1" />
          Mode dégradé
        </div>
      );
    }
  };
  
  return (
    <div className="relative" ref={dropdownRef}>
      <button
        className={`relative p-1 transition-colors ${
          wsConnected 
            ? 'text-gray-700 hover:text-primary-600' 
            : 'text-orange-500 hover:text-orange-700'
        }`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Notifications"
        title={wsConnected ? 'Notifications (Temps réel)' : 'Notifications (Mode dégradé)'}
      >
        <FaBell size={20} />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 -mt-1 -mr-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
        
        <div className={`absolute -bottom-1 -right-1 w-2 h-2 rounded-full ${
          wsConnected ? 'bg-green-400' : 'bg-orange-400'
        }`}></div>
      </button>
      
      {isOpen && (
        <div className="absolute right-0 z-20 mt-2 w-80 bg-white rounded-md shadow-lg border border-gray-200">
          <div className="p-2 border-b border-gray-200">
            <div className="flex justify-between items-center mb-2">
              <h3 className="font-medium">Notifications</h3>
              <div className="flex items-center space-x-2">
                <button
                  onClick={toggleSound}
                  className={`text-sm p-1 rounded-full ${
                    soundEnabled ? 'text-primary-600 hover:text-primary-800' : 'text-gray-400 hover:text-gray-600'
                  }`}
                  aria-label={soundEnabled ? "Désactiver les sons" : "Activer les sons"}
                  title={soundEnabled ? "Désactiver les sons" : "Activer les sons"}
                >
                  {soundEnabled ? <FaVolumeUp /> : <FaVolumeMute />}
                </button>
                
                {unreadCount > 0 && (
                  <button
                    onClick={handleMarkAllAsRead}
                    className="text-xs text-primary-600 hover:text-primary-800"
                  >
                    Tout marquer comme lu
                  </button>
                )}
                
                <button
                  onClick={() => loadNotifications(true)}
                  className="text-xs text-gray-600 hover:text-gray-800"
                  disabled={loading}
                  title="Actualiser"
                >
                  🔄
                </button>
              </div>
            </div>
            
            <div className="flex justify-between items-center">
              {getStatusIndicator()}
              <div className="text-xs text-gray-500">
                Source: {dataSource === 'cache' ? '📦 Cache' : 
                        dataSource === 'api' ? '🌐 API' : 
                        dataSource === 'websocket' ? '⚡ Temps réel' : 
                        '📦 Cache (obsolète)'}
              </div>
            </div>
          </div>
          
          <div className="max-h-64 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-gray-500">
                <div className="animate-spin inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full mr-2"></div>
                Chargement...
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                <FaBell className="mx-auto mb-2 text-gray-300" size={32} />
                Aucune notification
              </div>
            ) : (
              notifications.map(notification => (
                <div
                  key={notification._id}
                  className={`p-3 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors ${
                    getNotificationBgColor(notification)
                  }`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="flex justify-between items-start">
                    <div className="text-sm flex-1">
                      <div className="flex items-center mb-1">
                        {notification.type === 'mention' && (
                          <span className="inline-block bg-yellow-200 text-yellow-800 text-xs font-medium mr-2 px-2 py-1 rounded-full">
                            📢 Mention
                          </span>
                        )}
                        {notification.type === 'comment' && (
                          <span className="inline-block bg-green-200 text-green-800 text-xs font-medium mr-2 px-2 py-1 rounded-full">
                            💬 Commentaire
                          </span>
                        )}
                        {notification.type === 'system' && (
                          <span className="inline-block bg-blue-200 text-blue-800 text-xs font-medium mr-2 px-2 py-1 rounded-full">
                            🔔 Système
                          </span>
                        )}
                        
                        {!notification.read && (
                          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        )}
                      </div>
                      
                      <p className="text-gray-700">{notification.message}</p>
                    </div>
                    
                    <div className="text-xs text-gray-500 ml-2 whitespace-nowrap">
                      {formatDate(notification.createdAt, true)}
                    </div>
                  </div>
                  
                  <div className="mt-2 flex justify-between items-center">
                    {notification.contract && (
                      <div className="text-xs text-gray-600">
                        📄 Contrat: {typeof notification.contract === 'object' 
                          ? notification.contract.contractNumber 
                          : `ID: ${notification.contract}`}
                      </div>
                    )}
                    
                    {notification.sender && (
                      <div className="text-xs text-gray-500">
                        👤 {typeof notification.sender === 'object' 
                          ? notification.sender.username 
                          : notification.sender}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
          
          <div className="p-2 border-t border-gray-200 bg-gray-50">
            <div className="flex justify-between items-center text-xs">
              <span className="text-gray-500">
                {notifications.length} notification{notifications.length !== 1 ? 's' : ''}
                {unreadCount > 0 && ` • ${unreadCount} non lue${unreadCount !== 1 ? 's' : ''}`}
              </span>
              
              {currentUser?.role === 'admin' && (
                <button
                  onClick={async () => {
                    try {
                      const response = await fetch('/api/notifications/test', {
                        method: 'POST',
                        headers: {
                          'Authorization': `Bearer ${localStorage.getItem('token')}`
                        }
                      });
                      if (response.ok) {
                        console.log('✅ Notification de test envoyée');
                      }
                    } catch (error) {
                      console.error('❌ Erreur test notification:', error);
                    }
                  }}
                  className="text-primary-600 hover:text-primary-800 font-medium"
                  title="Envoyer une notification de test"
                >
                  🧪 Test
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;